import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import jspdf from 'jspdf';
import html2canvas from 'html2canvas';

// Fungsi untuk menambahkan Google Font ke dalam dokumen
const addGoogleFont = () => {
    const fontId = 'google-font-poppins';
    if (!document.getElementById(fontId)) {
        const link = document.createElement('link');
        link.id = fontId;
        link.rel = 'stylesheet';
        link.href = 'https://fonts.googleapis.com/css2?family=Poppins:wght@500;700&display=swap';
        document.head.appendChild(link);
    }
};

const InvoicePreview = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [invoice, setInvoice] = useState(null);
    const [loading, setLoading] = useState(true);
    const invoiceRef = useRef(null);

    useEffect(() => {
        addGoogleFont(); // Panggil fungsi untuk memuat font
        
        const fetchInvoice = async () => {
            const token = localStorage.getItem('token');
            try {
                const config = { headers: { 'x-auth-token': token } };
                const res = await axios.get(`https://planstopia-production.up.railway.app/api/invoices/${id}`, config);
                setInvoice(res.data);
            } catch (error) {
                console.error("Gagal mengambil data invoice:", error);
            } finally {
                setLoading(false);
            }
        };
        fetchInvoice();
    }, [id]);

    const handleDownloadPdf = () => {
        const input = invoiceRef.current;
        const buttons = input.querySelector('#print-buttons');
        if(buttons) buttons.style.display = 'none'; // Sembunyikan tombol sebelum print

        html2canvas(input, { 
            scale: 2,
            useCORS: true, 
            logging: false
        }).then((canvas) => {
            const imgData = canvas.toDataURL('image/png');
            const pdf = new jspdf('p', 'mm', 'a4');
            const pdfWidth = pdf.internal.pageSize.getWidth();
            const canvasWidth = canvas.width;
            const canvasHeight = canvas.height;
            const ratio = canvasWidth / canvasHeight;
            const imgWidth = pdfWidth; 
            const imgHeight = imgWidth / ratio;
            
            pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight);
            pdf.save(`invoice-${invoice.invoiceNumber}.pdf`);
            if(buttons) buttons.style.display = 'flex'; // Tampilkan kembali tombol
        });
    };
    
    if (loading) return <div style={styles.centered}>Memuat pratinjau...</div>;
    if (!invoice) return <div style={styles.centered}>Invoice tidak ditemukan.</div>;

    const formatDate = (date) => new Date(date).toLocaleDateString('id-ID', { day: '2-digit', month: 'long', year: 'numeric' });
    const formatCurrency = (num) => new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(num);

    return (
        <div style={styles.pageContainer}>
             <div id="print-buttons" style={styles.actions}>
                <button onClick={() => navigate('/invoices')} style={styles.actionButton}>Kembali</button>
                <button onClick={handleDownloadPdf} style={{...styles.actionButton, backgroundColor: '#10b981'}}>Unduh PDF</button>
            </div>

            {/* Konten Invoice yang akan dicetak */}
            <div ref={invoiceRef} style={styles.invoiceBox}>
                <header style={styles.header}>
                    <div style={styles.companyDetails}>
                        <img src="/assets/myLogo.png" alt="Logo Perusahaan" style={styles.logo} onError={(e) => { e.target.onerror = null; e.target.src='https://placehold.co/200x60/8daf7d/ffffff?text=Planstopia&font=poppins'; }} />
                        <p style={{ fontSize: '13px', margin: '2px 0' }}>Jl. Raya Manyaran - Gunungpati Km. 11</p>
                        <p style={{ fontSize: '13px', margin: '2px 0' }}>Semarang, 50229</p>
                        <p style={{ fontSize: '13px', margin: '2px 0' }}>Telp: 0877-0590-2302</p>
                    </div>
                    <div style={styles.invoiceDetails}>
                        <h2 style={styles.invoiceTitle}>INVOICE</h2>
                        <p style={styles.invoiceMeta}><strong>No:</strong> {invoice.invoiceNumber}</p>
                        <p style={styles.invoiceMeta}><strong>Date:</strong> {formatDate(invoice.dueDate)}</p>
                    </div>
                </header>
                
                <section style={styles.billingDetails}>
                    <div style={styles.billTo}>
                        <strong>KEPADA</strong>
                        <p style={{fontWeight: 'bold', margin: '10px 0 5px 0'}}>{invoice.clientName}</p>
                        <p style={{margin: 0}}>{invoice.clientAddress}</p>
                        <p style={{margin: '5px 0 0 0'}}>{invoice.clientContact}</p> {/* Diubah ke clientContact */}
                    </div>
                </section>

                <table style={styles.table}>
                    <thead>
                        <tr style={styles.tableHead}>
                            <th style={{...styles.th, textAlign: 'left', width: '50%'}}>Deskripsi</th>
                            <th style={styles.th}>Jumlah</th>
                            <th style={{...styles.th, textAlign: 'right'}}>Harga Satuan</th>
                            <th style={{...styles.th, textAlign: 'right'}}>Total</th>
                        </tr>
                    </thead>
                    <tbody>
                        {invoice.items.map((item, index) => (
                            <tr key={index} style={styles.tableRow}>
                                <td style={{...styles.td, textAlign: 'left'}}>{item.description}</td>
                                <td style={styles.td}>{item.quantity} kg</td>
                                <td style={{...styles.td, textAlign: 'right'}}>{formatCurrency(item.price)}</td>
                                <td style={{...styles.td, textAlign: 'right'}}>{formatCurrency(item.quantity * item.price)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                
                <section style={styles.summarySection}>
                    <div style={styles.notes}>
                        <strong>Catatan:</strong>
                        <p>{invoice.notes || 'Terima kasih atas kerja samanya.'}</p>
                    </div>
                    <table style={styles.totalsTable}>
                        <tbody>
                            <tr>
                                <td style={styles.totalsLabel}>Subtotal:</td>
                                <td style={styles.totalsValue}>{formatCurrency(invoice.subTotal)}</td>
                            </tr>
                            <tr>
                                <td style={styles.totalsLabel}>Pajak ({invoice.tax}%):</td>
                                <td style={styles.totalsValue}>{formatCurrency(invoice.subTotal * (invoice.tax / 100))}</td>
                            </tr>
                            <tr style={styles.totalRow}>
                                <td style={styles.totalsLabel}>TOTAL:</td>
                                <td style={styles.totalsValue}>{formatCurrency(invoice.total)}</td>
                            </tr>
                        </tbody>
                    </table>
                </section>
                
                <footer style={styles.footer}>
                    <p>Jika ada pertanyaan mengenai invoice ini,</p>
                    <p>silakan hubungi kami.</p>
                </footer>
            </div>
        </div>
    );
};

// CSS untuk Halaman Pratinjau yang Modern
const styles = {
    pageContainer: { backgroundColor: '#f3f4f6', padding: '40px', fontFamily: "'Poppins', 'Segoe UI', Roboto, Helvetica, Arial, sans-serif" },
    centered: { textAlign: 'center', padding: '50px' },
    actions: { display: 'flex', justifyContent: 'flex-end', gap: '10px', marginBottom: '20px' },
    actionButton: { padding: '10px 20px', border: 'none', borderRadius: '5px', cursor: 'pointer', backgroundColor: '#6b7280', color: 'white', fontWeight: 'bold' },
    invoiceBox: { maxWidth: '800px', margin: '0 auto', background: 'white', padding: '50px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' },
    header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', borderBottom: '2px solid #8daf7d', paddingBottom: '20px' },
    companyDetails: { color: '#4b5563', lineHeight: '1.4' },
    logo: { width: '200px', height: 'auto', marginBottom: '10px' },
    invoiceDetails: { textAlign: 'left' },
    invoiceTitle: { fontSize: '48px', color: '#8daf7d', margin: '0 0 5px 0', fontFamily: "'Poppins', sans-serif", fontWeight: '700' },
    invoiceMeta: { fontSize: '16px', margin: '2px 0', color: '#4b5563' },
    billingDetails: { display: 'flex', justifyContent: 'space-between', margin: '40px 0' },
    billTo: { maxWidth: '55%' },
    table: { width: '100%', borderCollapse: 'collapse', marginTop: '30px' },
    tableHead: { background: '#f3f4f6' },
    th: { padding: '15px', borderBottom: '1px solid #ddd', textAlign: 'center', color: '#4b5563', textTransform: 'uppercase', fontSize: '12px' },
    tableRow: { borderBottom: '1px solid #e5e7eb' },
    td: { padding: '15px', textAlign: 'center' },
    summarySection: { display: 'flex', justifyContent: 'space-between', marginTop: '40px', gap: '20px' },
    notes: { flex: 1, fontSize: '14px', maxWidth: '60%' },
    totalsTable: { width: '280px' },
    totalsLabel: { textAlign: 'right', padding: '8px', color: '#4b5563' },
    totalsValue: { textAlign: 'right', padding: '8px', fontWeight: 'bold' },
    totalRow: { borderTop: '2px solid #111827', fontSize: '20px', fontWeight: 'bold' },
    footer: { textAlign: 'center', marginTop: '50px', paddingTop: '20px', borderTop: '1px solid #e5e7eb', fontSize: '12px', color: '#6b7280' }
};

export default InvoicePreview;
